/**
 * Entity card block renderer.
 */

import { crmNavigation } from '../../../../services/crm-navigation.js';
import { t } from '../../../../i18n.js';

/**
 * @param {Object} block
 * @returns {HTMLElement}
 */
export function createEntityCard(block) {
  const { entityType, entityId, label, fields, canView } = block;

  const card = document.createElement('div');
  card.className = 'ai-entity-card';
  card.setAttribute('role', 'group');

  if (entityType === 'invalid' || entityId === null) {
    card.classList.add('ai-entity-card--invalid');
    card.setAttribute('aria-label', t('ai_restricted_record'));

    const header = document.createElement('div');
    header.className = 'ai-entity-card__header';

    const title = document.createElement('span');
    title.className = 'ai-entity-card__title ai-entity-card__title--muted';
    title.textContent = label;

    const noAccess = document.createElement('div');
    noAccess.className = 'ai-entity-card__no-access';
    noAccess.textContent = t('ai_no_access');

    header.appendChild(title);
    card.appendChild(header);
    card.appendChild(noAccess);
    return card;
  }

  card.setAttribute('aria-label', label);

  const header = document.createElement('div');
  header.className = 'ai-entity-card__header';

  const icon = document.createElement('span');
  icon.className = `ai-entity-card__icon${canView === false ? ' ai-entity-card__icon--muted' : ''}`;
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = (entityType || '?').slice(0, 1).toUpperCase();

  const titleEl = document.createElement(canView === false ? 'span' : 'a');
  titleEl.className = `ai-entity-card__title${canView === false ? ' ai-entity-card__title--muted' : ''}`;
  titleEl.textContent = label;

  if (canView !== false && crmNavigation.isNavigableEntityType(entityType)) {
    titleEl.href = '#';
    titleEl.addEventListener('click', (e) => {
      e.preventDefault();
      crmNavigation.openEntity(entityType, entityId);
    });
  } else if (canView === false) {
    titleEl.title = t('ai_no_access');
  }

  header.appendChild(icon);
  header.appendChild(titleEl);
  card.appendChild(header);

  if (fields?.length) {
    const dl = document.createElement('dl');
    dl.className = 'ai-entity-card__fields';

    fields.forEach((field) => {
      const row = document.createElement('div');
      row.className = 'ai-entity-card__field-row';

      const dt = document.createElement('dt');
      dt.className = 'ai-entity-card__field-label';
      dt.textContent = field.label;

      const dd = document.createElement('dd');
      dd.className = 'ai-entity-card__field-value-cell';
      dd.appendChild(createFieldValue(field));

      row.appendChild(dt);
      row.appendChild(dd);
      dl.appendChild(row);
    });

    card.appendChild(dl);
  }

  return card;
}

function createFieldValue(field) {
  const kind = field.kind || 'text';
  const wrap = document.createElement('span');
  wrap.className = fieldValueClass(kind);

  if (kind === 'email') {
    const a = document.createElement('a');
    a.href = `mailto:${field.value}`;
    a.className = wrap.className;
    a.textContent = field.value;
    return a;
  }

  if (kind === 'link') {
    const a = document.createElement('a');
    a.href = field.value;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.className = wrap.className;
    a.textContent = field.value;
    return a;
  }

  wrap.textContent = field.value;
  return wrap;
}

function fieldValueClass(kind) {
  switch (kind) {
    case 'currency':
    case 'number':
      return 'ai-entity-card__field-value ai-entity-card__field-value--numeric';
    case 'date':
      return 'ai-entity-card__field-value ai-entity-card__field-value--muted';
    case 'email':
    case 'link':
      return 'ai-entity-card__field-value ai-entity-card__field-value--link';
    default:
      return 'ai-entity-card__field-value';
  }
}
